import React from 'react'
import { useSiteMetadata } from '../hooks/useSiteMetadata'

export const Seo = ({ author, authorImage, children, cons, datePublished, dateModified, description, image, isBlogPost, pathname, pros, title }) => {
  const { companyName, description: defaultDescription, image: defaultImage, logo, twitterUsername, siteUrl } = useSiteMetadata()

  const seo = {
    author: author || twitterUsername,
    authorImage,
    companyName,
    description: description || defaultDescription,
    image: image || `${siteUrl}${defaultImage}`,
    logo: `${siteUrl}${logo}`,
    title: title || companyName,
    twitterUsername,
    url: `${siteUrl}${pathname || ``}`
  }

  const baseSchema = [
    {
      '@context': 'https://schema.org/',
      '@type': 'WebSite',
      url: seo.url,
      name: seo.companyName,
      description,
      sameAs: ['https://facebook.com/Parking4Less', 'https://twitter.com/Parking4less']
    },
    {
      '@context': 'https://schema.org/',
      '@type': 'Organization',
      url: seo.url,
      name: seo.companyName,
      sameAs: ['https://facebook.com/Parking4Less', 'https://twitter.com/Parking4less'],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Crawley, Gatwick',
        postalCode: 'RH6 0PA',
        streetAddress: 'The Beehive, Beehive Ring Rd'
      },
      logo: {
        '@type': 'ImageObject',
        url: seo.logo
      }
    }
  ]

  const schema = isBlogPost
    ? [
        ...baseSchema,
        {
          '@context': 'https://schema.org/',
          '@type': ['BlogPosting', 'NewsArticle', 'Article'],
          url: seo.url,
          name: seo.companyName,
          headline: seo.title,
          image: [seo.image],
          description: seo.description,
          author: {
            '@type': 'Person',
            name: seo.author
          },
          publisher: {
            '@type': 'Organization',
            url: seo.url,
            logo: {
              '@type': 'ImageObject',
              url: seo.authorImage
            },
            name: seo.author
          },
          mainEntityOfPage: {
            '@type': 'WebSite',
            '@id': seo.url
          },
          datePublished: datePublished,
          dateModified
        }
      ]
    : baseSchema

  let conJSON
  if (cons?.length > 0 && cons[0].text !== null) {
    conJSON = {
      negativeNotes: {
        '@type': 'ItemList',
        itemListElement: cons?.map((con, index) => {
          let conJSON = {
            '@type': 'ListItem',
            position: index + 1,
            name: con.text
          }
          return conJSON
        })
      }
    }
  }

  let proJSON
  if (pros?.length > 0 && pros[0].text !== null) {
    proJSON = {
      positiveNotes: {
        '@type': 'ItemList',
        itemListElement: pros?.map((pro, index) => {
          let proJSON = {
            '@type': 'ListItem',
            position: index + 1,
            name: pro.text
          }
          return proJSON
        })
      }
    }
  }

  const prosAndCons = [
    {
      '@context': 'http://schema.org',
      '@type': 'Product',
      name: seo.title,
      review: {
        '@type': 'Review',
        name: `${seo.title} review`,
        author: {
          '@type': 'Person',
          name: 'Mark Holmes'
        },
        ...conJSON,
        ...proJSON
      }
    }
  ]

  return (
    <>
      <title>{seo.title}</title>
      <meta name='title' content={seo.title} />
      <meta name='author' content={seo.author} />
      <link rel='canonical' href={seo.url} data-baseprotocol='https:' data-basehost={siteUrl} />
      <meta name='description' content={seo.description} />
      <meta name='image' content={seo.image} />
      <meta property='og:title' content={seo.title} />
      <meta property='og:description' content={seo.description} />
      <meta property='og:image' content={`${seo.image}?&fit=crop&w=1200&h=630&crop=edges`} />
      <meta property='og:image:width' content='1200' />
      <meta property='og:image:height' content='630' />
      <meta property='og:type' content='website' />
      <meta property='og:url' content={seo.url} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={seo.title} />
      <meta property='twitter:url' content={seo.url} />
      <meta property='twitter:domain' content='parking4less.com' />
      <meta name='twitter:description' content={seo.description} />
      <meta name='twitter:image' content={`${seo.image}?&fit=crop&wi=1200&h=630&crop=edges`} />
      <meta name='twitter:creator' content={seo.twitterUsername} />
      <script type='application/ld+json'>{JSON.stringify(schema)}</script>
      {proJSON && conJSON && prosAndCons && <script type='application/ld+json'>{JSON.stringify(prosAndCons)}</script>}
      {children}
    </>
  )
}

export default Seo
